import { Navigate, useLocation } from 'react-router-dom';

import { canAccess, posOperatorHome, posOperatorMayVisit } from '@/lib/access';
import type { Role } from '@/lib/auth-context';
import type { ModuleKey } from '@/lib/modules';
import { useAuth } from '@/lib/use-auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  module?: ModuleKey;
  /** Any one of these grants admits — a page with a tab per grant. */
  anyModule?: ModuleKey[];
  /** With `anyModule`: an administrator is admitted regardless, for the tabs that are theirs. */
  orAdmin?: boolean;
  adminOnly?: boolean;
  /** Restricts the route to a specific set of roles (in addition to `adminOnly`/`module`). */
  roles?: Role[];
}

export function ProtectedRoute({ children, module, anyModule, orAdmin, adminOnly, roles }: ProtectedRouteProps) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user.isPosOperator) {
    return posOperatorMayVisit(user, location.pathname) ? (
      <>{children}</>
    ) : (
      <Navigate to={posOperatorHome(user)} replace />
    );
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  if (module && !canAccess(user, module)) {
    return <Navigate to="/" replace />;
  }

  if (anyModule && !((orAdmin && user.role === 'admin') || anyModule.some(m => canAccess(user, m)))) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
